import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMessageBox } from "../components/MessageBox";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { servicePlansService } from "../services/servicePlansService";
// Token costs are no longer hardcoded - backend is source of truth
import type {
  UserSubscription,
  ServicePlan,
} from "../../../shared/types/servicePlans";
import "./SubscriptionSummary.css";

interface SubscriptionSummaryProps {
  onPlanChange?: () => void;
}

interface TokenCost {
  operation: string;
  operationKey: string;
  points: number;
  description: string;
}

export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = () => {
  const navigate = useNavigate();
  const { showMessageBox } = useMessageBox();
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null,
  );
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [tokenCosts, setTokenCosts] = useState<TokenCost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load subscription, plans, and token costs
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [subscriptionData, plansData, tokenCostsData] = await Promise.all([
        servicePlansService.getUserSubscription().catch(() => null),
        servicePlansService.getServicePlans(),
        servicePlansService.getTokenCosts().catch(() => []),
      ]);
      setSubscription(subscriptionData);
      setPlans(plansData.sort((a, b) => parseInt(a.id) - parseInt(b.id)));
      setTokenCosts(tokenCostsData);
    } catch (error: any) {
      console.error("Error loading subscription data:", error);
      if (error.status !== 404) {
        showMessageBox({
          type: "error",
          title: "Error",
          message: error.message || "Failed to load subscription data",
          buttons: ["ok"],
          onButtonClick: () => {},
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []); // Only run once on mount

  useEffect(() => {
    loadData();
  }, []); // Only run once on mount

  if (isLoading) {
    return (
      <div className="subscription-summary">
        <LoadingSpinner />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-summary">
        <div className="subscription-summary-card">
          <div className="subscription-summary-header">
            <h3 className="subscription-summary-title">Token Balance</h3>
            <button
              className="btn btn-primary btn-manage"
              onClick={() => navigate("/service-plans")}
            >
              Purchase Tokens
            </button>
          </div>
          <div className="subscription-summary-content">
            <p className="no-subscription-message">No active subscription</p>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = plans.find((p) => p.id === subscription.currentPlanId);

  return (
    <div className="subscription-summary">
      <div className="subscription-summary-card">
        <div className="subscription-summary-header">
          <div className="subscription-summary-title-section">
            <span className="subscription-summary-plan-name">
              {currentPlan?.serviceName || "N/A"}
            </span>
            <span className="subscription-summary-subtitle">Token Costs</span>
          </div>
          <button
            className="btn btn-primary btn-manage"
            onClick={() => navigate("/service-plans")}
          >
            Manage
          </button>
        </div>

        <div className="subscription-summary-content">
          <div className="subscription-summary-limits">
            {tokenCosts.length > 0 ? (
              tokenCosts.map((item, index) => (
                <div key={item.operationKey || index} className="limit-row">
                  <span className="limit-label">
                    {item.description?.trim() || item.operation}
                  </span>
                  <span className="limit-value">{item.points} points</span>
                </div>
              ))
            ) : (
              <div className="limit-row">
                <span className="limit-label">Loading token costs...</span>
                <span className="limit-value">-</span>
              </div>
            )}
          </div>

          {subscription.pendingPlanId && (
            <div className="subscription-summary-pending">
              <span className="pending-badge">Pending</span>
              <span className="pending-text">
                Changes to{" "}
                {plans.find((p) => p.id === subscription.pendingPlanId)
                  ?.serviceName || "N/A"}{" "}
                on{" "}
                {new Date(subscription.nextBillingDate).toLocaleDateString(
                  "en-US",
                  {
                    month: "short",
                    day: "numeric",
                    timeZone: "UTC", // Force UTC to prevent date shifting
                  },
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
